import { CantileverButton } from 'components/CantileverButton'
import dayjs from 'dayjs'
import { TimeRange } from 'features/systemLens'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { SentinelCalculatingDots } from 'features/timeSeries/components/SentinelCalculatingIndicator'
import { TimeSeriesLoadingDots } from 'features/timeSeries/components/timeSeriesScatterplot/TimeSeriesLoadingDots'
import { useSentinelScoringActive } from 'features/timeSeries/hooks/useSentinelOverlayMap'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { TimeSeriesService } from 'features/timeSeries/timeSeriesService'
import {
  canNavigateForward,
  getNavigatedTimeRange,
  navigateTimeRange,
} from 'features/timeSeries/utils/activeTimeRangeHelpers'
import {
  formatSampleCadence,
  isSpanAtMaxZoom,
} from 'features/timeSeries/utils/maxZoom'
import { estimateSampleIntervalMs } from 'features/timeSeries/utils/sampleCadence'
import {
  ChevronLeft,
  ChevronRight,
  PanelRight,
  RotateCcw,
  SlidersHorizontal,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'

import { ActiveTimeRangeSectionWithPopover } from './ActiveTimeRangeSectionWithPopover'

const ContainerQueryWrapper = styled.div`
  container-type: inline-size;
`

const ActiveRangeDisplayContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  position: relative;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  padding: 0.0625rem 0.25rem;
  padding-left: 4rem;
  padding-right: 4rem;
  border-radius: 0.375rem;

  div[data-role='right-actions'] {
    position: absolute;
    right: 0.25rem;
    display: flex;
    align-items: center;
  }
`

const TimeDisplayRow = styled.div`
  display: flex;
  justify-content: center;

  @container (max-width: 500px) {
    width: 100%;
  }
`

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

// Reserve the slot so the button row does not shift when loading toggles.
const LoadingSlot = styled.div<{ $isVisible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  width: 5.25rem;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;
  pointer-events: none;
`

const LoadingLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

// Informational only — pan/zoom-out/presets all keep working at the floor, so
// this must read as a status, not a disabled control.
const MaxZoomTag = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ theme }) => theme.forecast.secondaryText};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.25rem;
  padding: 0 0.375rem;
  margin-left: 0.25rem;
`

const StyledIcon = css`
  width: 1rem;
  height: 1rem;
`

const StyledZoomIn = styled(ZoomIn)`
  ${StyledIcon}
`
const StyledZoomOut = styled(ZoomOut)`
  ${StyledIcon}
`
const StyledChevronLeft = styled(ChevronLeft)`
  ${StyledIcon}
`
const StyledChevronRight = styled(ChevronRight)`
  ${StyledIcon}
`
const StyledSettings = styled(SlidersHorizontal)`
  ${StyledIcon}
`
const StyledRotateCcw = styled(RotateCcw)`
  ${StyledIcon}
`
const StyledPanelRight = styled(PanelRight)`
  ${StyledIcon}
`

// Lighter persistent background signals the associated panel is open.
const PanelToggleButton = styled(CantileverButton)<{ $open?: boolean }>`
  ${({ $open, theme }) =>
    $open &&
    css`
      background-color: ${theme.forecast.hoverGhostButton};
    `}
`

interface ActiveTimeRangeSelectorProps {
  timeRange: TimeRange
  setTimeRange: React.Dispatch<React.SetStateAction<TimeRange>>
  onClickBackward?: () => void
  onClickForward?: () => void
  liveTail?: boolean
  onRangeClick?: () => void
  onSettingsClick?: () => void
  onResetViewport?: () => void
  onPenDrawerClick?: () => void
  isSettingsOpen?: boolean
  isPenDrawerOpen?: boolean
}

export const ActiveTimeRangeSelector: React.FC<
  ActiveTimeRangeSelectorProps
> = ({
  timeRange,
  setTimeRange,
  onClickBackward,
  onClickForward,
  liveTail,
  onRangeClick,
  onSettingsClick,
  onResetViewport,
  onPenDrawerClick,
  isSettingsOpen = false,
  isPenDrawerOpen = false,
}) => {
  const {
    chartData,
    dataLayers,
    isLoading,
    prefetchTimeRange,
    selectedSensors,
    exitLiveTail,
  } = useScatterplotContext()
  const isSentinelScoring = useSentinelScoringActive()
  const [showDateConfig, setShowDateConfig] = useState(false)
  const { fromTime, toTime } = useMemo(() => {
    return { fromTime: dayjs(timeRange.from), toTime: dayjs(timeRange.to) }
  }, [timeRange.from, timeRange.to])
  const hasEnabledSensorLayer = useMemo(
    () => dataLayers.some((layer) => layer.type === 'sensor' && layer.enabled),
    [dataLayers]
  )

  // "Max zoom" status: the committed window sits at the data-derived zoom floor
  // (`clampTimeRange` expands any narrower gesture to it), so zooming in
  // further only re-centers. Same cadence estimate the box-zoom floor uses
  // (`useChartViewportInteractions.minZoomSpanRef`), so the tag and the clamp
  // can never disagree about where the floor is. Gated on data being present:
  // with nothing on screen there is no cadence and no honest claim to make.
  const sampleCadenceMs = useMemo(() => {
    if (chartData.length === 0) return null
    return estimateSampleIntervalMs(
      chartData
        .map((point) => point.timestamp)
        .filter(
          (timestamp): timestamp is number => typeof timestamp === 'number'
        )
    )
  }, [chartData])
  const isAtMaxZoom = useMemo(() => {
    if (chartData.length === 0) return false
    return isSpanAtMaxZoom(
      toTime.valueOf() - fromTime.valueOf(),
      sampleCadenceMs
    )
  }, [chartData.length, fromTime, toTime, sampleCadenceMs])

  // Ensure timeRange always has interval/intervalUnit computed. This must be
  // the SAME interval buildTimeSeriesRequest derives for an interval-less
  // range (same helper, same pen count, same fractional seconds), otherwise
  // backfilling it changes the request signature and refetches the window the
  // initial fetch already loaded.
  useEffect(() => {
    if (!timeRange.interval || !timeRange.intervalUnit) {
      const timeRangeSeconds = (toTime.valueOf() - fromTime.valueOf()) / 1000
      const { interval, interval_unit } =
        TimeSeriesService.getIntervalForPenCount(
          timeRangeSeconds,
          selectedSensors.length
        )
      setTimeRange({
        ...timeRange,
        interval,
        intervalUnit: interval_unit,
      })
    }
  }, [setTimeRange, timeRange, fromTime, toTime, selectedSensors.length])

  useEffect(() => {
    if (
      isLoading ||
      liveTail ||
      selectedSensors.length === 0 ||
      !hasEnabledSensorLayer
    ) {
      return
    }

    // Prefetched ranges bake their interval too, so they must use the same
    // pen count as the fetch a real navigation would issue.
    const navOptions = { penCount: selectedSensors.length }
    const rangesToPrefetch = [
      getNavigatedTimeRange(
        'backward',
        false,
        fromTime,
        toTime,
        dayjs(),
        navOptions
      ),
      ...(canNavigateForward(toTime)
        ? [
            getNavigatedTimeRange(
              'forward',
              false,
              fromTime,
              toTime,
              dayjs(),
              navOptions
            ),
          ]
        : []),
      getNavigatedTimeRange(
        'backward',
        true,
        fromTime,
        toTime,
        dayjs(),
        navOptions
      ),
      getNavigatedTimeRange(
        'forward',
        true,
        fromTime,
        toTime,
        dayjs(),
        navOptions
      ),
    ]

    void Promise.all(
      rangesToPrefetch.map((range) => prefetchTimeRange(range))
    ).catch(() => undefined)
  }, [
    fromTime,
    hasEnabledSensorLayer,
    isLoading,
    liveTail,
    prefetchTimeRange,
    selectedSensors.length,
    toTime,
  ])

  const handleBackwardClick = useCallback(() => {
    exitLiveTail()
    navigateTimeRange('backward', false, fromTime, toTime, setTimeRange, {
      penCount: selectedSensors.length,
    })
    onClickBackward?.()
  }, [
    fromTime,
    toTime,
    setTimeRange,
    onClickBackward,
    exitLiveTail,
    selectedSensors.length,
  ])

  const handleForwardClick = useCallback(() => {
    exitLiveTail()
    navigateTimeRange('forward', false, fromTime, toTime, setTimeRange, {
      penCount: selectedSensors.length,
    })
    onClickForward?.()
  }, [
    fromTime,
    toTime,
    setTimeRange,
    onClickForward,
    exitLiveTail,
    selectedSensors.length,
  ])

  const handleRangeClick = useCallback(() => {
    if (onRangeClick) {
      onRangeClick()
    } else {
      setShowDateConfig(!showDateConfig)
    }
  }, [onRangeClick, showDateConfig])

  const handlePopoverOpenChange = useCallback((open: boolean) => {
    setShowDateConfig(open)
  }, [])

  const handleZoomOutClick = useCallback(() => {
    exitLiveTail()
    navigateTimeRange('backward', true, fromTime, toTime, setTimeRange, {
      penCount: selectedSensors.length,
    })
  }, [fromTime, toTime, setTimeRange, exitLiveTail, selectedSensors.length])

  const handleZoomInClick = useCallback(() => {
    exitLiveTail()
    navigateTimeRange('forward', true, fromTime, toTime, setTimeRange, {
      penCount: selectedSensors.length,
    })
  }, [fromTime, toTime, setTimeRange, exitLiveTail, selectedSensors.length])

  return (
    <>
      <ContainerQueryWrapper>
        <ActiveRangeDisplayContainer>
          <TimeDisplayRow>
            <ActiveTimeRangeSectionWithPopover
              isCustom={timeRange.preset === 'custom' && liveTail !== true}
              onClick={handleRangeClick}
              fromTime={fromTime}
              toTime={toTime}
              timeRange={timeRange}
              onRangeClick={onRangeClick}
              showDateConfig={showDateConfig}
              onOpenChange={handlePopoverOpenChange}
              onClose={() => setShowDateConfig(false)}
            />
          </TimeDisplayRow>

          <ControlsRow>
            {onResetViewport && (
              <ChartTooltip label='Reset to saved date' side='top'>
                <CantileverButton
                  onClick={onResetViewport}
                  ghost
                  iconOnly
                  aria-label='Reset to saved date'>
                  <StyledRotateCcw />
                </CantileverButton>
              </ChartTooltip>
            )}

            <ChartTooltip label='Previous time period' side='top'>
              <CantileverButton
                onClick={handleBackwardClick}
                ghost
                iconOnly
                aria-label='Previous time period'>
                <StyledChevronLeft />
              </CantileverButton>
            </ChartTooltip>
            <ChartTooltip label='Next time period' side='top'>
              <CantileverButton
                onClick={handleForwardClick}
                ghost
                iconOnly
                aria-label='Next time period'
                disabled={!canNavigateForward(toTime)}>
                <StyledChevronRight />
              </CantileverButton>
            </ChartTooltip>

            <ChartTooltip label='Zoom Out' side='top'>
              <CantileverButton
                ghost
                iconOnly
                aria-label='Zoom Out'
                onClick={handleZoomOutClick}>
                <StyledZoomOut />
              </CantileverButton>
            </ChartTooltip>
            <ChartTooltip label='Zoom In' side='top'>
              <CantileverButton
                ghost
                iconOnly
                aria-label='Zoom In'
                onClick={handleZoomInClick}>
                <StyledZoomIn />
              </CantileverButton>
            </ChartTooltip>

            {isAtMaxZoom && (
              <ChartTooltip
                label={`This window is at the data's resolution — about one sample every ${formatSampleCadence(sampleCadenceMs)}. Zooming in further won't reveal more detail; panning and zooming out still work.`}
                side='top'>
                <MaxZoomTag role='status' aria-label='Max zoom reached'>
                  Max zoom
                </MaxZoomTag>
              </ChartTooltip>
            )}

            <LoadingSlot
              $isVisible={isLoading || isSentinelScoring}
              data-testid='time-series-loading-indicator'>
              {/* Data loading takes precedence over sentinel scoring. */}
              {isLoading ? (
                <>
                  <LoadingLabel>Loading</LoadingLabel>
                  <TimeSeriesLoadingDots />
                </>
              ) : isSentinelScoring ? (
                <>
                  <LoadingLabel>Calculating</LoadingLabel>
                  <SentinelCalculatingDots />
                </>
              ) : null}
            </LoadingSlot>
          </ControlsRow>

          {(onSettingsClick || onPenDrawerClick) && (
            <div data-role='right-actions'>
              {onSettingsClick && (
                <ChartTooltip label='Edit pens' side='top'>
                  <PanelToggleButton
                    onClick={onSettingsClick}
                    ghost
                    iconOnly
                    aria-label='Edit pens'
                    aria-pressed={isSettingsOpen}
                    $open={isSettingsOpen}>
                    <StyledSettings />
                  </PanelToggleButton>
                </ChartTooltip>
              )}
              {onPenDrawerClick && (
                <ChartTooltip label='Show tag names' side='top'>
                  <PanelToggleButton
                    onClick={onPenDrawerClick}
                    ghost
                    iconOnly
                    aria-label='Show tag names'
                    aria-pressed={isPenDrawerOpen}
                    $open={isPenDrawerOpen}>
                    <StyledPanelRight />
                  </PanelToggleButton>
                </ChartTooltip>
              )}
            </div>
          )}
        </ActiveRangeDisplayContainer>
      </ContainerQueryWrapper>
    </>
  )
}
